import { Button } from '@/components/ui/button';
import { useListAssets } from '@/features/assets/api/list-assets';
import { useListAssetsByService } from '@/features/assets/api/list-assets-by-service';
import {
  type AssetContentTypeTag,
  assetContentTypeTagLabels,
} from '@borradh-workspace/api-client/types';
import { Check, ImageIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ContentSelector } from './content-selector';
import {
  type PickerAsset,
  buildImagePickerLibrary,
  filterImagePickerLibrary,
} from './image-picker-library';

interface ImagePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onConfirm: (ids: string[]) => void;
  onSelectionChange?: (ids: string[]) => void;
  serviceId?: string | null;
  minCount?: number;
  maxCount?: number;
  /** Overridden when the picker is filling ONE named slot ("Slide 3"). */
  title?: string;
  confirmLabel?: string;
  /**
   * Show exactly these assets instead of the whole library. Claire's
   * "which of these did you mean?" card already knows the candidate set; the
   * picker must not swap it for everything the org has ever uploaded.
   */
  assets?: PickerAsset[];
  libraryPage?: {
    loaded: number;
    total: number;
    isLoading: boolean;
    isError: boolean;
    onLoadMore: () => void;
    onRetry: () => void;
  };
}

type FilterKey = 'all' | AssetContentTypeTag;
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  ...(
    Object.entries(assetContentTypeTagLabels) as [AssetContentTypeTag, string][]
  ).map(([key, label]) => ({ key, label })),
];

export function ImagePickerDialog({
  open,
  onOpenChange,
  selectedIds,
  onConfirm,
  onSelectionChange,
  serviceId,
  minCount = 1,
  maxCount = 10,
  title = 'Choose images',
  confirmLabel = 'Use selected images',
  assets,
  libraryPage,
}: ImagePickerDialogProps) {
  const [localSelected, setLocalSelected] = useState(selectedIds);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const {
    assets: allImages,
    isLoading: loadingAll,
    isError: failedAll,
    refetch,
  } = useListAssets({
    type: 'image',
    source: 'raw',
    limit: 100,
  });
  const { assets: serviceAssets, isLoading: loadingService } =
    useListAssetsByService(serviceId ?? '', 'image');

  useEffect(() => {
    if (open) setLocalSelected(selectedIds);
  }, [open, selectedIds]);

  const { ordered: libraryOrdered, serviceLinked } = useMemo(
    () => buildImagePickerLibrary(allImages, serviceAssets),
    [allImages, serviceAssets]
  );
  // An explicit candidate set wins over the fetched library.
  const ordered = assets ?? libraryOrdered;
  const linked = assets ? new Set<string>() : serviceLinked;
  const visible = useMemo(
    // `filterImagePickerLibrary` only reads name/tags; an explicit candidate
    // set carries the same fields but not the rest of `Asset`.
    () =>
      filterImagePickerLibrary(
        ordered as Parameters<typeof filterImagePickerLibrary>[0],
        filter,
        search
      ),
    [ordered, filter, search]
  );
  const isLoading = assets
    ? false
    : loadingAll || (serviceId ? loadingService : false);

  const closeWithoutSaving = () => {
    setLocalSelected(selectedIds);
    onOpenChange(false);
  };
  const toggle = (id: string) => {
    const next = localSelected.includes(id)
      ? localSelected.filter((item) => item !== id)
      : // A one-slot picker REPLACES; it does not refuse. The guard below is
        // right for "up to ten images" and wrong for "this slide's image" —
        // there, clicking a second photo would do nothing at all, which reads
        // as a broken grid rather than as a limit.
        maxCount === 1
        ? [id]
        : localSelected.length >= maxCount
          ? localSelected
          : [...localSelected, id];
    setLocalSelected(next);
    onSelectionChange?.(next);
  };

  return (
    <ContentSelector
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      search={search}
      onSearchChange={setSearch}
      filters={FILTERS}
      filter={filter}
      onFilterChange={setFilter}
      filterLabel="Filter images"
      isLoading={isLoading}
      isEmpty={visible.length === 0 && !libraryPage && !(failedAll && !assets)}
      empty={
        <div className="flex flex-col items-center py-12 text-center text-sm text-muted-foreground">
          <ImageIcon className="mb-3 size-8 opacity-40" />
          <p>No uploaded images match this filter.</p>
        </div>
      }
      footerNote={
        maxCount === 1 ? (
          <>
            {localSelected.length === 1
              ? 'One image chosen.'
              : 'Pick one image.'}
          </>
        ) : (
          <>
            <span className="font-medium text-foreground">
              {localSelected.length}
            </span>{' '}
            selected · maximum {maxCount}
          </>
        )
      }
      confirmLabel={confirmLabel}
      confirmDisabled={localSelected.length < minCount}
      onConfirm={() => {
        onConfirm(localSelected);
        onOpenChange(false);
      }}
      onCancel={closeWithoutSaving}
    >
      {libraryPage && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>
            {libraryPage.loaded} of {libraryPage.total} photos loaded
          </span>
          {libraryPage.isError ? (
            <Button size="sm" variant="outline" onClick={libraryPage.onRetry}>
              Retry photos
            </Button>
          ) : (
            libraryPage.loaded < libraryPage.total && (
              <Button
                size="sm"
                variant="outline"
                disabled={libraryPage.isLoading}
                onClick={libraryPage.onLoadMore}
              >
                {libraryPage.isLoading ? 'Loading…' : 'Load more photos'}
              </Button>
            )
          )}
        </div>
      )}
      {failedAll && !assets ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 py-12 text-center text-sm text-muted-foreground"
        >
          <ImageIcon className="size-8 opacity-40" />
          <p>Your photo library could not be loaded.</p>
          <button
            type="button"
            className="rounded border px-3 py-1.5 text-sm"
            onClick={() => {
              void refetch();
            }}
          >
            Retry library
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {visible.map((asset) => {
            const selected = localSelected.includes(asset.id);
            return (
              <button
                key={asset.id}
                type="button"
                onClick={() => toggle(asset.id)}
                aria-pressed={selected}
                aria-label={`${selected ? 'Deselect' : 'Select'} ${asset.name}`}
                className={`overflow-hidden rounded-lg border-2 text-left transition-colors ${
                  selected
                    ? 'border-primary bg-primary/5'
                    : 'border-transparent hover:border-muted-foreground/20'
                }`}
              >
                <div className="relative aspect-[4/5] bg-muted">
                  {asset.blobUrl || asset.thumbnailUrl ? (
                    <img
                      src={asset.thumbnailUrl ?? asset.blobUrl ?? undefined}
                      alt=""
                      className="size-full object-cover"
                    />
                  ) : (
                    <div className="grid size-full place-items-center">
                      <ImageIcon className="size-8 text-muted-foreground/40" />
                    </div>
                  )}
                  <span
                    className={`absolute left-1.5 top-1.5 grid size-5 place-items-center rounded border shadow-sm ${
                      selected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-white/80 bg-white text-transparent'
                    }`}
                  >
                    <Check className="size-3.5" />
                  </span>
                  {linked.has(asset.id) && (
                    <span className="absolute right-1.5 top-1.5 rounded bg-primary/90 px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground">
                      Service match
                    </span>
                  )}
                </div>
                <p className="truncate p-2 text-xs font-medium">{asset.name}</p>
              </button>
            );
          })}
        </div>
      )}
    </ContentSelector>
  );
}
